/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2021, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./LassoSelectAPI',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
], function (
	LassoSelectAPI, APIFactory,
) {
	const KPI_SPARKLINE_SELECTOR = 'kpi-widget-sparkline';
	return class LassoSelect {
		constructor(options) {
			/**
			 * The features that are passed into the Lasso Select feature.
			 * @member {Object} _features
			 */
			this._features = options.features;
			/**
			 * API Instance for Lasso Select feature.
			 * @member {APIInstance} _api
			 */
			this._api = APIFactory.createAPI(this, [LassoSelectAPI]);
			/**
			 * Function to be called when activating the Lasso Select.
			 * @member {function(event) : void} _startListener
			 */
			this._startListener = this._start.bind(this);
			/**
			 * Function to be called when deactivating the Lasso Select.
			 * @member {function() : void} _stopListener
			 */
			this._stopListener = this._stop.bind(this);
			/**
			 * Function to be called when moving the Lasso Select.
			 * @member {function(event) : void} _scanListener
			 */
			this._scanListener = this._scan.bind(this);
			/**
			 * Extra padding in pixels used in determining 
			 * whether to activate Lasso Select
			 * @member {Integer} _activateTolerance
			 */
			this._activateTolerance = 2;
			/**
			 * Array that holds the ids of the widgets to 
			 * be selected when the Lasso select is active.
			 * @member {String[]} _lassoedWidgets
			 */
			this._lassoedWidgets = [];
			/**
			 * Determines whether or not the Lasso is running in
			 * Aggregated selection mode.
			 * @member {Boolean} _aggregateResults
			 */
			this._aggregateResults = false;

			this._startListenerCallbacks = {};
		}

		getAPI() {
			return this._api;
		}

		register(element, tabLevelContent) {
			if (element) {
				const startListenerCallback = this._startListener.bind(this, tabLevelContent);
				this._startListenerCallbacks[tabLevelContent.getId()] = startListenerCallback;
				element.addEventListener('mousedown', startListenerCallback);
				element.addEventListener('mouseup', this._stopListener);
				element.addEventListener('mousemove', this._scanListener);
			}
		}

		deregister(element, tabLevelContent) {
			if (element) {
				element.removeEventListener('mousedown', this._startListenerCallbacks[tabLevelContent.getId()]);
				delete this._startListenerCallbacks[tabLevelContent.getId()];
				element.removeEventListener('mouseup', this._stopListener);
				element.removeEventListener('mousemove', this._scanListener);
			}
			if(this.lassoWidget) {
				this.lassoWidget.remove();
				delete this.lassoWidget;
			}
		}

		/**
		 * Private method called when user mouses down on canvas.
		 * @param {object} Content of the tab container
		 * @param {MouseEvent} event 
		 */
		_start(tabLevelContent, event) {
			// Get tab div and cache it
			this._tab = event.target.closest('.pagecontainer');
			if(this._isMousedownWithinAnyContent(event, tabLevelContent)) {
				return; // do not start lasso
			}
			if (this._tab) {
				// disable text selection on all widgets in tab
				const widgets = this._tab.getElementsByClassName('widget');
				for (const widget of widgets) {
					if (widget) {
						widget.style.userSelect = 'none';
					}
				}
				this._selectedWidgets = this._features['Canvas'].getSelectedContentList().map(x => x.getId());
				// If user is on Mac, use Command key to activate aggregate mode, otherwise use Ctrl
				if (window.navigator.userAgent.match('Mac')) {
					this._aggregateResults = event.metaKey;
				} else {
					this._aggregateResults = event.ctrlKey;
				}
				const expandHandles = Array.from(this._tab.getElementsByClassName('expandHandle'));
				const moveHandles = Array.from(this._tab.getElementsByClassName('moveHandle'));
				const handles = expandHandles.concat(moveHandles);
				const xClick = event.pageX;
				const yClick = event.pageY;
				// for each widget, if the click is on a widget, then don't start the lasso
				for (let i = 0; i < widgets.length; i++) {
					const widget = widgets[i];
					if (widget) {
						const boundingRect = widget.getBoundingClientRect();
						if (this._isClickInRect(xClick, yClick, boundingRect)) {
							return;
						}
					}
				}
				// if the cursor is on an expand or move handle, then don't start the lasso
				for (const handle of handles) {
					if (handle) {
						const boundingRect = handle.getBoundingClientRect();
						if (this._isClickInRect(xClick, yClick, boundingRect)) {
							return;
						}
					}
				}
				this.scanning = true;
				this.x = event.pageX;
				this.y = event.pageY;
				if (this.lassoWidget) {
					this.lassoWidget.style.setProperty('display', 'inherit');
					this.lassoWidget.classList.remove('hidden');
				} else {
					this.lassoWidget = document.createElement('div');
					this.lassoWidget.className = 'com_ibm_cognos_lasso';
				}
				const parentRect = this._tab.offsetParent.getBoundingClientRect();
				this.lassoWidget.style.top = this.y + this._tab.scrollTop - parentRect.top + 'px';
				this.lassoWidget.style.left = this.x + this._tab.scrollLeft - parentRect.left + 'px';
				this._tab.appendChild(this.lassoWidget);
			}
		}

		_stop() {
			this.scanning = false;
			if (this._lassoedWidgets) {
				const lassoResultSet = new Set();
				const refinedAggregatedWidgets = [...this._selectedWidgets];
				for (const widget of this._lassoedWidgets) {
					const widgetAPI = this._features['Canvas'].getContent(widget);
					if (widgetAPI) {
						const parent = widgetAPI.getContainer();
						if (parent.getType() === 'group') {
							if (this._selectedWidgets.includes(parent.getId())) {
								lassoResultSet.add(widgetAPI.getId());
								if (this._aggregateResults) {
									const index = refinedAggregatedWidgets.indexOf(parent.getId());
									if (index !== -1) {
										refinedAggregatedWidgets.splice(index, 1);
										this._features['Canvas'].deselectContent([parent.getId()]);
									}
								}
							} else {
								const children = this._features['Canvas'].getContent(parent.getId()).getChildren();
								const atLeastOneSelected = children.filter(c => this._selectedWidgets.includes(c.getId())).length > 0;
								if (atLeastOneSelected) {
									lassoResultSet.add(widgetAPI.getId());
								} else {
									lassoResultSet.add(parent.getId());
								}
							}
						} else {
							lassoResultSet.add(widgetAPI.getId());
						}
					}
				}
				if (this._aggregateResults) {
					refinedAggregatedWidgets.forEach(widget => lassoResultSet.add(widget));
				}
				if (lassoResultSet.size > 0) {
					this._features['Canvas'].selectContent(Array.from(lassoResultSet));
				}
				this._lassoedWidgets = [];
				this._aggregateResults = false;
			}

			if (this._tab) {
				const widgets = this._tab.getElementsByClassName('widget');
				for (const widget of widgets) {
					if (widget) {
						widget.style.userSelect = 'auto';
					}
				}
			}

			if (this.lassoWidget) {
				// using the size css to avoid the show/hide flashing
				this.lassoWidget.style.setProperty('width', '0');
				this.lassoWidget.style.setProperty('height', '0');
				this.lassoWidget.classList.add('hidden');
			}
		}

		_scan(event) {
			if (this.scanning) {
				this.finX = event.pageX;
				this.finY = event.pageY;
				const newWidth = Math.abs(this.finX - this.x);
				const newHeight = Math.abs(this.finY - this.y);
				this.lassoWidget.style.setProperty('left', (Math.min(this.x, this.finX) + this._tab.scrollLeft - this._tab.offsetParent.getBoundingClientRect().left) + 'px');
				this.lassoWidget.style.setProperty('top', (Math.min(this.y, this.finY) + this._tab.scrollTop - this._tab.offsetParent.getBoundingClientRect().top) + 'px');
				this.lassoWidget.style.setProperty('width', newWidth + 'px', 'important');
				this.lassoWidget.style.setProperty('height', newHeight + 'px', 'important');
				const widgets = Array.from(this._tab.querySelectorAll(`.widget:not(.${KPI_SPARKLINE_SELECTOR})`)); // KPI's spark line view a widget within a widget. 
				widgets.forEach((widget) => {
					const widgetAPI = this._features['Canvas'].getContent(widget.id);
					if (widgetAPI) {
						const selectable = widgetAPI.getPropertyValue('selectable') !== false;
						if (selectable) {
							if (widget && widget.getBoundingClientRect && this.lassoWidget) {
								if (this._shouldSelectWidget(widget)) {
									if (this._lassoedWidgets.indexOf(widgetAPI.getId()) === -1) {
										this._lassoedWidgets.push(widgetAPI.getId());
									}
								} else {
									const index = this._lassoedWidgets.indexOf(widgetAPI.getId());
									if (index !== -1) {
										this._lassoedWidgets.splice(index, index + 1);
									}
								}
							}
						}
					}
				});
			}
		}

		_isClickInRect(xClick, yClick, boundingRect) {
			return ((xClick >= boundingRect.x - this._activateTolerance && xClick <= boundingRect.right + this._activateTolerance) &&
				(yClick >= boundingRect.y - this._activateTolerance && yClick <= boundingRect.bottom + this._activateTolerance));
		}

		_shouldSelectWidget(widgetDOM) {
			if (this.lassoWidget) {
				const lassoWidgetDOMRect = this.lassoWidget.getBoundingClientRect();
				const widgetDOMRect = widgetDOM.getBoundingClientRect();
				const xSelectPadding = widgetDOMRect.width * .1;
				const ySelectPadding = widgetDOMRect.height * .1;
				return ((lassoWidgetDOMRect.left > widgetDOMRect.left + xSelectPadding && lassoWidgetDOMRect.left < widgetDOMRect.right - xSelectPadding) ||
					(lassoWidgetDOMRect.left < widgetDOMRect.left + xSelectPadding && lassoWidgetDOMRect.right > widgetDOMRect.left + xSelectPadding)) &&
					((lassoWidgetDOMRect.top > widgetDOMRect.top + ySelectPadding && lassoWidgetDOMRect.top < widgetDOMRect.bottom - ySelectPadding) ||
						(lassoWidgetDOMRect.top < widgetDOMRect.top + ySelectPadding && lassoWidgetDOMRect.bottom > widgetDOMRect.top + ySelectPadding)
					);
			}
			return false;
		}

		_isMousedownWithinAnyContent(event, tabLevelContent) {
			let isMousedownWithinContent = false;
			const contentsInTab = tabLevelContent.findContent();
			if (contentsInTab.length) {
				contentsInTab.some(content => {
					const contentViewDom = content.getFeature('ContentViewDOM');
					const contentNode = contentViewDom && contentViewDom.getNode();
					if (contentNode) {
						isMousedownWithinContent = contentNode.contains(event.target);
						return isMousedownWithinContent === true; // as long as we find one, short-circuit the loop
					}
				});
			}
			return isMousedownWithinContent;
		}

		isAggregatedSelectInProgress() {
			return !!this._aggregateResults;
		}
	};
});
